import { useEffect } from "react";
import type { SEOMeta } from "@/types/content";

interface SEOProps {
  meta: SEOMeta;
}

export default function SEO({ meta }: SEOProps) {
  useEffect(() => {
    const createdElements: HTMLElement[] = [];

    const setMeta = (selector: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        const attr = selector.includes("property")
          ? "property"
          : "name";
        const key = selector.includes("property")
          ? selector.replace('meta[property="', "").replace('"]', "")
          : selector.replace('meta[name="', "").replace('"]', "");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        createdElements.push(el);
      }
      el.setAttribute("content", content);
    };

    if (meta.title) {
      document.title = meta.title;
    }

    if (meta.description) {
      setMeta('meta[name="description"]', meta.description);
    }
    if (meta.keywords && meta.keywords.length > 0) {
      setMeta('meta[name="keywords"]', meta.keywords.join(", "));
    }
    if (meta.canonical) {
      let link = document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
        createdElements.push(link);
      }
      link.setAttribute("href", meta.canonical);
    }

    if (meta.noindex) {
      setMeta('meta[name="robots"]', "noindex, nofollow");
    } else {
      // remove noindex if previously set
      const robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (robotsMeta && robotsMeta.getAttribute("content")?.includes("noindex")) {
        robotsMeta.remove();
      }
    }

    // Open Graph
    if (meta.title) {
      setMeta('meta[property="og:title"]', meta.title);
    }
    if (meta.description) {
      setMeta('meta[property="og:description"]', meta.description);
    }
    setMeta('meta[property="og:type"]', meta.ogType || "website");
    if (meta.canonical) {
      setMeta('meta[property="og:url"]', meta.canonical);
    }
    if (meta.ogImage) {
      setMeta('meta[property="og:image"]', meta.ogImage);
    }

    // Twitter Card
    setMeta('meta[name="twitter:card"]', "summary_large_image");
    if (meta.title) {
      setMeta('meta[name="twitter:title"]', meta.title);
    }
    if (meta.description) {
      setMeta('meta[name="twitter:description"]', meta.description);
    }
    if (meta.ogImage) {
      setMeta('meta[name="twitter:image"]', meta.ogImage);
    }

    // Structured data
    if (meta.structuredData) {
      let script = document.querySelector(
        "script[data-seo-structured]"
      ) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("data-seo-structured", "true");
        document.head.appendChild(script);
        createdElements.push(script);
      }
      script.textContent = JSON.stringify(meta.structuredData);
    } else {
      const existingScript = document.querySelector("script[data-seo-structured]") as HTMLScriptElement | null;
      if (existingScript) {
        existingScript.remove();
      }
    }

    return () => {
      createdElements.forEach((el) => el.remove());
    };
  }, [meta]);

  return null;
}